import { forwardRef, type ComponentPropsWithoutRef, type ElementType } from 'react';

import { cx } from '../internal/cx.js';
import { buildSpacingStyle, pickSpacingProps, type SpacingProps } from '../internal/spacing.js';

export type FlexDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse';
export type FlexAlign = 'start' | 'center' | 'end' | 'baseline' | 'stretch';
export type FlexJustify = 'start' | 'center' | 'end' | 'between';

const DIRECTION_CLASS: Record<FlexDirection, string> = {
  row: 'nrx-flex--row',
  column: 'nrx-flex--column',
  'row-reverse': 'nrx-flex--row-reverse',
  'column-reverse': 'nrx-flex--column-reverse',
};

const ALIGN_CLASS: Record<FlexAlign, string> = {
  start: 'nrx-items-start',
  center: 'nrx-items-center',
  end: 'nrx-items-end',
  baseline: 'nrx-items-baseline',
  stretch: 'nrx-items-stretch',
};

const JUSTIFY_CLASS: Record<FlexJustify, string> = {
  start: 'nrx-justify-start',
  center: 'nrx-justify-center',
  end: 'nrx-justify-end',
  between: 'nrx-justify-between',
};

export type FlexProps = ComponentPropsWithoutRef<'div'> &
  SpacingProps & {
    /** Polymorphic element tag. */
    as?: ElementType;
    direction?: FlexDirection;
    align?: FlexAlign;
    justify?: FlexJustify;
    wrap?: boolean;
  };

/** Flexbox layout primitive. Requires `@import '@nrx/ui/theme.css'`. */
export const Flex = forwardRef<HTMLElement, FlexProps>(function Flex(
  {
    as,
    className,
    style,
    direction = 'row',
    align,
    justify,
    wrap,
    gap,
    gapX,
    gapY,
    p,
    px,
    py,
    pt,
    pr,
    pb,
    pl,
    m,
    mx,
    my,
    mt,
    mr,
    mb,
    ml,
    ...rest
  },
  ref,
): React.JSX.Element {
  const Component = as ?? 'div';
  const spacingStyle = buildSpacingStyle(
    pickSpacingProps({
      gap,
      gapX,
      gapY,
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml,
    }),
  );

  return (
    <Component
      ref={ref as never}
      className={cx(
        'nrx-flex',
        DIRECTION_CLASS[direction],
        align && ALIGN_CLASS[align],
        justify && JUSTIFY_CLASS[justify],
        wrap && 'nrx-flex--wrap',
        className,
      )}
      style={{ ...spacingStyle, ...style }}
      {...rest}
    />
  );
});
